// My Organisation - Manage organization settings and members
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useAuth0Login } from "~/hooks/useAuth0Login";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiClient } from "~/lib/orpc-client";
import { Spinner } from "~/components/ui/spinner";
import { useState } from "react";
import {
  Users,
  Mail,
  UserPlus,
  Trash2,
  Check,
  Shield,
  User,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import z from "zod";
import InviteMemberModal from "~/components/organization/InviteMemberModal";
import RemoveMemberModal from "~/components/organization/RemoveMemberModal";
import RevokeInvitationModal from "~/components/organization/RevokeInvitationModal";
import { QUERY_KEYS } from "~/constants/queryKeys";

export const Route = createFileRoute("/_protected/my-organisation")({
  validateSearch: z.object({
    create: z.boolean().optional(),
  }),
  component: MyOrganisationPage,
});

function MyOrganisationPage() {
  const { isAuthenticated, isLoading, user } = useAuth0Login();
  const {
    capabilities,
    routing,
    isLoading: isUserLoading,
  } = useAuthenticatedUser();

  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ userId: string; name: string; email?: string } | null>(null);
  const [invitationToRevoke, setInvitationToRevoke] = useState<{ id: string; email: string } | null>(null);

  // Fetch current user's organization
  const {
    data: orgData,
    isLoading: isLoadingOrg,
  } = useQuery({
    queryKey: [QUERY_KEYS.MY_ORGANIZATION],
    queryFn: () => apiClient.organizations.getMyOrganization(),
    enabled: isAuthenticated,
  });

  // Fetch organization members
  const {
    data: membersData,
    isLoading: isLoadingMembers,
    refetch: refetchMembers,
  } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION_MEMBERS, orgData?.organization?.id],
    queryFn: () =>
      apiClient.organizations.listMembers({
        orgId: orgData!.organization!.id,
      }),
    enabled: !!orgData?.organization?.id,
  });

  // Fetch pending invitations
  const {
    data: invitationsData,
    refetch: refetchInvitations,
  } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION_INVITATIONS, orgData?.organization?.id],
    queryFn: () =>
      apiClient.organizations.listInvitations({
        orgId: orgData!.organization!.id,
      }),
    enabled: !!orgData?.organization?.id,
  });

  const resendInvitationMutation = useMutation({
    mutationFn: ({ invitationId }: { invitationId: string; email: string }) =>
      apiClient.organizations.resendInvitation({
        orgId: orgData!.organization!.id,
        invitationId,
      }),
    onMutate: ({ email }) => {
      setResendingEmail(email);
    },
    onSuccess: () => {
      refetchInvitations();
      setInviteSuccess("Invitation resent successfully");
      setTimeout(() => setInviteSuccess(null), 5000);
    },
    onSettled: () => {
      setResendingEmail(null);
    },
  });

  // Update member role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: "ORGANIZATION_ADMINISTRATOR" | "ORGANIZATION_MEMBER" }) => {
      return apiClient.organizations.updateMemberRole({
        orgId: orgData!.organization!.id,
        userId,
        role,
      });
    },
    onSuccess: () => {
      refetchMembers();
    },
  });

  // Get current user's Auth0 ID for self-check
  const currentUserAuth0Id = user?.sub;

  // Loading state
  if (isLoading || isUserLoading || isLoadingOrg) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Spinner className="size-8 text-brand-green-primary" />
      </div>
    );
  }

  if (!capabilities?.canManageOrganization) {
    return <Navigate to={(routing?.homePath ?? "/dashboard")} replace />;
  }

  const org = orgData?.organization;

  if (!org) {
    return <div>No organization found</div>;
  }

  const members = membersData?.members || [];

  // Check if current user is an org admin (uses backend-computed isOrgAdmin flag)
  const currentUserMember = members.find(m => m.userId === currentUserAuth0Id);
  const isCurrentUserAdmin = currentUserMember?.isOrgAdmin ?? false;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">


          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">My Organization: {org.displayName}</h1>
            </div>
          </div>
        </div>

        {/* Success message for invites */}
        {inviteSuccess && (
          <div className="mb-6 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm max-w-xl">
            <Check className="w-4 h-4" />
            {inviteSuccess}
          </div>
        )}

        <div className="grid gap-6 max-w-xl">
          {/* Members List */}
          <Card>

            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Members ({members.length})
                  </CardTitle>
                  <CardDescription>
                    People who are part of this organization
                  </CardDescription>
                </div>
                {isCurrentUserAdmin && (
                <Button
                  size="sm"
                  onClick={() => setShowInviteModal(true)}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite a New Member
                </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingMembers ? (
                <div className="flex justify-center py-8">
                  <Spinner className="w-6 h-6 text-brand-green-primary" />
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-8 text-foreground/70">
                  <Users className="w-12 h-12 mx-auto mb-3 text-foreground/30" />
                  <p>No members yet</p>
                  <p className="text-sm">
                    Invite people to join your organization
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {members.map((member) => {
                    const isAdmin = member.isOrgAdmin ?? false;
                    const isSelf = member.userId === currentUserAuth0Id;

                    return (
                      <div
                        key={member.userId}
                        className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                      >
                        <div className="flex items-center gap-3">
                          {member.picture ? (
                            <img
                              src={member.picture}
                              alt={member.name || member.email || "Member"}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-brand-green-light/30 flex items-center justify-center">
                              <span className="text-brand-green-primary font-medium">
                                {(member.name || member.email || "?")?.[0]?.toUpperCase() || "?"}
                              </span>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">
                                {member.name || "Unknown"}
                                {isSelf && (
                                  <span className="ml-2 text-xs text-black font-normal">(Me)</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Role dropdown - editable by org admins or system admins (for non-self) */}
                          {isCurrentUserAdmin && !isSelf ? (
                            <select
                              className="text-xs border rounded-lg px-2 py-1 bg-white"
                              value={isAdmin ? "ORGANIZATION_ADMINISTRATOR" : "ORGANIZATION_MEMBER"}
                              onChange={(e) => {
                                const newRole = e.target.value as "ORGANIZATION_ADMINISTRATOR" | "ORGANIZATION_MEMBER";
                                updateRoleMutation.mutate({ userId: member.userId, role: newRole });
                              }}
                              disabled={updateRoleMutation.isPending}
                            >
                              <option value="ORGANIZATION_ADMINISTRATOR">Administrator</option>
                              <option value="ORGANIZATION_MEMBER">Member</option>
                            </select>
                          ) : (
                            // Read-only badge for self or non-admins
                            isAdmin ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                <Shield className="w-3 h-3" />
                                Administrator
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-foreground">
                                <User className="w-3 h-3" />
                                Member
                              </span>
                            )
                          )}
                          {/* Remove button - for org admins or system admins (not self) */}
                          {!isSelf && isCurrentUserAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setMemberToRemove({
                                userId: member.userId,
                                name: member.name || member.email || "Unknown",
                                email: member.email,
                              })}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pending Invitations */}
              {invitationsData?.invitations && invitationsData.invitations.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Pending Invitations ({invitationsData.invitations.length})
                  </h4>
                  <div className="space-y-3">
                    {invitationsData.invitations.map((invitation) => (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-foreground">{invitation.email}</p>
                          <p className="text-xs text-foreground/70">
                            Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isCurrentUserAdmin && (
                            <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resendInvitationMutation.mutate({ invitationId: invitation.id, email: invitation.email })}
                            disabled={resendInvitationMutation.isPending}
                          >
                                {resendInvitationMutation.isPending && resendingEmail === invitation.email
                                  ? "Resending..."
                                  : "Resend"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setInvitationToRevoke({ id: invitation.id, email: invitation.email })}
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          >
                            Revoke
                          </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delete Organization — only visible to org admins */}
          {isCurrentUserAdmin && (
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-base">Delete Organization</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-foreground">
                  To delete your organization, please contact support at{" "}
                  <a
                    href="mailto:support@proudlyhuman.org"
                    className="text-brand-green-primary hover:underline font-medium"
                  >
                    support@proudlyhuman.org
                  </a>
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <InviteMemberModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        orgId={org.id}
        orgDisplayName={org.displayName}
        userEmail={user?.email}
        onSuccess={(message) => {
          setInviteSuccess(message);
          refetchInvitations();
          setTimeout(() => setInviteSuccess(null), 5000);
        }}
      />

      <RemoveMemberModal
        open={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        orgId={org.id}
        orgDisplayName={org.displayName}
        member={memberToRemove}
        members={members}
        onSuccess={refetchMembers}
      />

      <RevokeInvitationModal
        open={!!invitationToRevoke}
        onClose={() => setInvitationToRevoke(null)}
        orgId={org.id}
        invitation={invitationToRevoke}
        onSuccess={refetchInvitations}
      />
    </div>
  );
}
